import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ChevronRight,
  CircleAlert,
  LoaderCircle,
  X,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  fetchAgentList,
  fetchJobApplications,
  fetchRequirementById,
} from '../hooks/queries';
import { updateRequirement } from '../hooks/mutations';
import { BtnDefault } from '../components/common/Buttons';
import OpenAssetButton from '../components/common/OpenAssetButton';
import { IAgent } from '../types/agent';
import { formatDate } from '../utils';
import { AgentCard, AgentDetailCard, Detail } from '../components/AgentCard';
import { IoArrowBack } from 'react-icons/io5';
import { general, requirementStatusOptions } from '../constants';
import Select from '../components/common/Select';

const AGENTS_PER_PAGE = 6;

const RcseView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [showMore, setShowMore] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: requirement,
    isLoading,
    isError,
    refetch,
  } = fetchRequirementById({ id: id || '' });
  const { data: agentsData } = fetchAgentList({});
  const { data: applicationsData } = fetchJobApplications({
    startRow: 0,
    endRow: general.ROWS_TO_FETCH,
    status: 'InterviewPending',
  });

  const { mutate: updateRequirementMutation, isPending: isUpdating } =
    updateRequirement({
      onSuccess: () => {
        toast.success('Requirement updated');
        refetch();
      },
    });

  const allOptions = useMemo(() => {
    return [
      ...(agentsData?.agents || []),
      ...(applicationsData?.rows || []),
    ].map((a) => ({
      label: a.name || a.personalInfo?.firstName || 'Unnamed Agent',
      value: a._id,
    }));
  }, [agentsData, applicationsData]);

  const isAgentSelected = (agentId: string) =>
    requirement?.agents?.some((a: any) => (a?.agentId || a._id) === agentId);

  const toggleAgent = (agentId: string) => {
    const currentAgents = requirement?.agents || [];
    const updatedAgents = isAgentSelected(agentId)
      ? currentAgents.filter((a: any) => (a?.agentId || a._id) !== agentId)
      : [...currentAgents, agentId];

    updateRequirementMutation({
      _id: requirement?._id || '',
      agents: updatedAgents,
    });
  };

  const handleAgentSelect = (agent: IAgent) => {
    setSelectedAgent(agent);
    if (!isAgentSelected(agent._id)) toggleAgent(agent._id);
  };

  const paginatedMatches = useMemo(() => {
    const matches = requirement?.matches || [];
    const start = (currentPage - 1) * AGENTS_PER_PAGE;
    return matches.slice(start, start + AGENTS_PER_PAGE);
  }, [requirement?.matches, currentPage]);

  const totalPages = Math.ceil(
    (requirement?.matches?.length || 0) / AGENTS_PER_PAGE
  );

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateRequirementMutation({
      _id: requirement?._id || '',
      status: e.target.value as any,
    });
  };

  const formattedDays = (days: string[] = []) =>
    days
      .map((d) => d.slice(0, 3).charAt(0).toUpperCase() + d.slice(1, 3))
      .join(', ');

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      fulfilled: 'bg-green-100 text-green-700 focus:ring-green-300',
      sourced: 'bg-blue-100 text-blue-700 focus:ring-blue-300',
      active: 'bg-yellow-100 text-yellow-700 focus:ring-yellow-300',
      postponed: 'bg-orange-100 text-orange-700 focus:ring-orange-300',
      dropped: 'bg-red-100 text-red-700 focus:ring-red-300',
      reopened: 'bg-purple-100 text-purple-700 focus:ring-purple-300',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700 focus:ring-gray-300';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoaderCircle size={36} className="animate-spin text-greyNinety" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center gap-2 h-screen text-red-500">
        <CircleAlert size={20} /> <p>Something went wrong.</p>
      </div>
    );
  }

  const renderAgentCards = (agents: any[]) =>
    agents.map((agent) => {
      const agentId = agent.agentId || agent._id;
      return (
        <AgentCard
          key={agentId}
          agent={agent}
          onClick={() => setSelectedAgent(agent)}
          isSelected={isAgentSelected(agentId)}
          onToggle={() => toggleAgent(agentId)}
        />
      );
    });

  return (
    <div className="relative max-w-6xl mx-auto">
      <BtnDefault
        onClick={() => navigate('/clients/rcse')}
        className="flex items-center gap-1 my-4"
      >
        <IoArrowBack className="w-3 h-3" />
        Back
      </BtnDefault>

      <div className="bg-white rounded-xl shadow p-6 space-y-4 border border-greyFifty">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">
              {requirement?.clientName || '--'}
            </h2>
            <p className="text-gray-600 mt-2">{requirement?.email || '--'}</p>
            <p className="text-gray-600">
              Days: {formattedDays(requirement?.days)}
            </p>
            <p className="text-gray-600">
              Working Hours: {requirement?.workingStart || '--'} –{' '}
              {requirement?.workingEnd || '--'}
            </p>
          </div>

          <select
            value={requirement?.status || ''}
            onChange={handleStatusChange}
            disabled={isUpdating}
            className={`text-sm font-medium px-3 py-1 rounded-md capitalize transition border focus:outline-none focus:ring-2 ${getStatusColor(requirement?.status || '')}`}
          >
            {requirementStatusOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {showMore && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
            {[
              ['Client POC', requirement?.clientPOC],
              ['Country', requirement?.country],
              ['Industry', requirement?.industry],
              [
                'Relevant Industries',
                requirement?.relevantIndustries?.join(', '),
              ],
              ['Channels', requirement?.channels?.join(', ')],
              ['Tools', requirement?.tools?.join(', ')],
              ['Languages', requirement?.languages?.join(', ')],
              ['Type', requirement?.type],
              ['Timezone', requirement?.timezone],
              ['Website', requirement?.website || '--'],
              ['Priority', requirement?.priority],
              [
                'Engagement',
                requirement?.engagement != null
                  ? String(requirement.engagement)
                  : '--',
              ],
              ['Tickets', requirement?.tickets?.toString()],
              ['Expected Hiring By', formatDate(requirement?.expectedHiringBy)],
              ['Created At', formatDate(requirement?.createdAt)],
              ['Updated At', formatDate(requirement?.updatedAt)],
              ['Comments', requirement?.comments || '--'],
              ['Raised By', requirement?.raisedBy],
            ].map(([label, value]) => (
              <Detail key={label} label={label} value={value} />
            ))}
          </div>
        )}

        {showMore && requirement?.attachments?.length ? (
          <div className="pt-4 border-t">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Attachments
            </p>
            <div className="flex flex-wrap gap-2">
              {requirement.attachments.map((attachment) => (
                <OpenAssetButton
                  key={attachment.fileKey}
                  assetKey={attachment.fileKey}
                  name="Open"
                />
              ))}
            </div>
          </div>
        ) : null}

        <button
          onClick={() => setShowMore(!showMore)}
          className="text-blue-600 text-sm font-medium flex items-center gap-1"
        >
          {showMore ? (
            <>
              Show Less <ChevronUp size={20} />
            </>
          ) : (
            <>
              Show More <ChevronDown size={20} />
            </>
          )}
        </button>
      </div>

      <div className="space-y-4 mt-4">
        <h3 className="text-xl font-semibold">Matched Agents</h3>
        {requirement?.agents?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {renderAgentCards(requirement.agents)}
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 text-greyNinety text-sm">
            No matches yet, add an agent.
          </div>
        )}
      </div>

      <div className="space-y-4 mt-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold">Top Recommended Agents</h3>
          <div className="w-72">
            <Select
              isMulti={false}
              options={allOptions}
              value={allOptions.find((a) => a.value === selectedAgent?._id)}
              onChange={(newValue) => {
                const agent = [
                  ...(agentsData?.agents || []),
                  ...(applicationsData?.rows || []),
                ].find((a) => a._id === newValue?.value);
                if (agent) handleAgentSelect(agent);
              }}
              placeholder="Choose an agent..."
            />
          </div>
        </div>

        {requirement?.matches?.length ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {renderAgentCards(paginatedMatches)}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-4 mt-6">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-white hover:bg-greyTen border rounded disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <span className="text-sm self-center">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-white hover:bg-greyTen border rounded disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-48 text-greyNinety text-sm">
            No matched agents found.
          </div>
        )}
      </div>

      {selectedAgent && (
        <div
          onClick={() => setSelectedAgent(null)}
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl relative"
          >
            <button
              className="absolute top-2 right-3 text-gray-500 hover:text-gray-700 hover:bg-greyThirty rounded-full p-1"
              onClick={() => setSelectedAgent(null)}
            >
              <X size={20} />
            </button>
            <AgentDetailCard agent={selectedAgent} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RcseView;
